<template>
  <validation-observer
    v-if="showLicenceNumber"
    v-show="isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      id="licence-number"
      :validated="formObserver.validated"
      v-bind="observer"
    >
      <form-row :label="questionLabel">
        <template slot="helpText">
          <div class="d-flex align-start">
            <v-icon
              class="mr-4"
              color="primary"
            >
              mdi-credit-card-outline
            </v-icon>
            <p>
              <strong>Price sensitive question</strong><br>
              {{ computedHelpText }}
            </p>
          </div>
        </template>
        <validation-provider
          v-slot="{ failed }"
          :vid="`${stepId}_licence-number_yes_no`"
          name="Provide Licence Number"
          rules="required"
        >
          <radio
            v-model="questionData.licenceNumberYesNo"
            :name="`${stepId}.licence-number-yes-no`"
            :data-test="fieldTrackerId.yesNo"
            :items="constants.YES_NO"
            :error="failed"
          />
        </validation-provider>
      </form-row>
      <form-row v-if="questionData.licenceNumberYesNo">
        <template slot="helpText">
          <p><strong>Where?</strong> You can find your licence number in section 5 of {{ !isAdditional ? 'your' : 'their' }} driving licence. It contains 16 characters and begins with letters from {{ !isAdditional ? 'your' : 'their' }} surname.</p>
          <p><strong>Who?</strong> This is only applicable to those holding GB licences (i.e. licences issued in mainland England, Scotland or Wales).</p>
        </template>
        <div class="licence-number">
          <div class="col">
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_licence-number-1`"
              name="Licence Number (part 1)"
              :rules="drivingLicenceNumberRules[1]"
            >
              <qz-text-field
                ref="licenceNumber1"
                v-model="licence.number[1]"
                :data-test="fieldTrackerId['1']"
                :error="failed"
                :maxlength="drivingLicenceNumberRules[1].length"
                @keyup="onLicenceNumberKeyUp(1)"
                @focus="onLicenceNumberFocus($event, 1)"
                @paste="onLicencePaste($event, 1)"
              />
            </validation-provider>
          </div>
          <div class="col">
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_licence-number-2`"
              name="Licence Number (part 2)"
              :rules="drivingLicenceNumberRules[2]"
            >
              <qz-number-input
                ref="licenceNumber2"
                v-model="licence.number[2]"
                :data-test="fieldTrackerId['2']"
                :error="failed"
                :maxlength="drivingLicenceNumberRules[2].length"
                :without-separators="true"
                @keyup="onLicenceNumberKeyUp(2)"
                @focus="onLicenceNumberFocus($event, 2)"
                @paste="onLicencePaste($event, 2)"
              />
            </validation-provider>
          </div>
          <div class="col">
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_licence-number-3`"
              name="Licence Number (part 3)"
              :rules="drivingLicenceNumberRules[3]"
            >
              <qz-text-field
                ref="licenceNumber3"
                v-model="licence.number[3]"
                :data-test="fieldTrackerId['3']"
                :error="failed"
                :maxlength="drivingLicenceNumberRules[3].length"
                @keyup="onLicenceNumberKeyUp(3)"
                @focus="onLicenceNumberFocus($event, 3)"
                @paste="onLicencePaste($event, 3)"
              />
            </validation-provider>
          </div>
          <div class="col">
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_licence-number-4`"
              name="Licence Number (part 4)"
              :rules="drivingLicenceNumberRules[4]"
            >
              <qz-text-field
                ref="licenceNumber4"
                v-model="licence.number[4]"
                :data-test="fieldTrackerId['4']"
                :error="failed"
                :maxlength="drivingLicenceNumberRules[4].length"
                @keydown="onLicenceNumberKeyDown"
                @keyup="onLicenceNumberKeyUp(4)"
                @focus="onLicenceNumberFocus($event, 4)"
                @paste="onLicencePaste($event, 4)"
              />
            </validation-provider>
          </div>
        </div>
        <validation-provider
          v-slot="{ failed, errors }"
          :vid="`${stepId}_licence-number`"
          :rules="{
            licenceNumber: {
              isDrivingLicenceNumberValidationNew,
              partial: true,
            },
          }"
          immediate
        >
          <input
            type="hidden"
            :value="questionData.licenceNumber"
          >
          <div
            v-if="failed && formObserver.validated && questionData.licenceNumber"
            class="error--text mt-3 mb-3"
          >
            <p>{{ errors[0] }}</p>
          </div>
        </validation-provider>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import { validate } from 'vee-validate';
import { ExtDate } from '@/helpers/date';
import { limitValueChars } from '@/helpers/common';
import {
  licenceNumberRules,
  licenceNumberRulesUpdated,
  licencePrefillNameRegex,
} from '@/helpers/products/car';
import QuestionBase from './Base';
import {
  inject,
  computed,
} from '@vue/composition-api';
import {
  padEnd,
  padStart,
  snakeCase,
} from 'lodash';

const LICENCE_NUMBER_MAX_LENGTH = 16;

export default {
  name: 'question-licence-number',
  extends: QuestionBase,
  data () {
    // TODO set condition for next products
    const { licenceNumber } = this.formData.drivers[this.stepIndex];

    const licenceNumberParts = {
      1: '',
      2: '',
      3: '',
      4: '',
    };

    if (licenceNumber) {
      let licenceNumberTemp = licenceNumber.replace(/ +/g, '');
      Object.keys(licenceNumberRules)
        .sort()
        .forEach(key => {
          const valuePart = licenceNumberTemp.substr(0, licenceNumberRules[key].length);
          licenceNumberTemp = licenceNumberTemp.substr(valuePart.length);
          licenceNumberParts[key] = valuePart;
        });
    }

    return {
      fields: [
        'licenceNumber',
        'licenceNumberYesNo',
      ],
      licence: {
        number: licenceNumberParts,
      },
    };
  },
  computed: {
    isNorthernIrelandPostcode () {
      // if the driver is additional check the postcode of the main driver on the quote
      if (this.isAdditional) {
        return this.formData?.drivers[0]?.postcode?.indexOf('BT') === 0;
      }
      return this.stepData?.postcode?.indexOf('BT') === 0;
    },
    questionLabel () {
      if (!this.isAdditional) {
        return this.title || 'Would you like to provide your Driving Licence Number, which may reduce your premium with some insurance providers on our panel?';
      }
      return this.titleAdditional || this.title || 'Would you like to provide their Driving Licence Number, which may reduce your premium with some insurance providers on our panel?';
    },
    computedHelpText () {
      if (!this.isAdditional) {
        return this.helpText ? this.helpText.value || this.helpText : 'Some of our insurance providers may give a discount if you provide your driving licence number here.';
      }
      return this.helpText ? this.helpText.valueAdditional || this.helpText.value || this.helpText : 'Some of our insurance providers may give a discount if you provide their driving licence number here.';
    },
    isLicenceHidden () {
      return this.config.hiddenQuestions?.['licence-number'] === 1;
    },
    showLicenceNumber () {
      if (this.isLicenceHidden) return false;
      return (
        this.constants.DRIVER_TYPE_OF_DRIVING_UK.includes(this.stepData.typeOfDriving) &&
        !this.isNorthernIrelandPostcode
      );
    },
    fieldTrackerId () {
      return {
        yesNo: snakeCase(this.questionFields.licenceNumberYesNo),
        1: `${snakeCase(this.questionFields.licenceNumber)}_1`,
        2: `${snakeCase(this.questionFields.licenceNumber)}_2`,
        3: `${snakeCase(this.questionFields.licenceNumber)}_3`,
        4: `${snakeCase(this.questionFields.licenceNumber)}_4`,
      };
    },
  },
  created () {
    if (!this.questionData.licenceNumber || this.questionData.licenceNumber.length < LICENCE_NUMBER_MAX_LENGTH) {
      this.prefillLicence();
    }

    // for cni partner
    this.$watch('isNorthernIrelandPostcode', val => {
      if (val) {
        this.questionData.licenceNumber = null;
        this.questionData.licenceNumberYesNo = false;
      }
    });

    this.initLicenceWatchers();
  },
  methods: {
    initLicenceWatchers () {
      this.$watch('stepData.surname', () => this.prefillLicence(1));
      this.$watch('stepData.dateOfBirth', () => this.prefillLicence(2));
      this.$watch('stepData.title', () => this.prefillLicence(2));

      this.$watch('questionData.licenceNumberYesNo', async (val) => {
        if (!val) {
          this.questionData.licenceNumber = null;
          return;
        }

        this.prefillLicence();

        await this.$nextTick();

        let str = '';
        Object.keys(this.licence.number).sort().forEach(key => {
          str += padEnd(this.licence.number[key], this.drivingLicenceNumberRules[key].length);
        });
        this.questionData.licenceNumber = str || null;
      });

      this.$watch('licence.number', async (val) => {
        if (!this.questionData.licenceNumberYesNo) return;
        let str = '';
        Object.keys(val).sort().forEach(key => {
          str += padEnd(val[key], this.drivingLicenceNumberRules[key].length);
        });
        this.questionData.licenceNumber = str || null;
      }, { deep: true });
    },
    limitLicenseNumberInputChars (n) {
      this.licence.number[n] = (limitValueChars(this.licence.number[n], this.drivingLicenceNumberRules[n].length))();
    },
    onLicenceNumberKeyDown (event) {
      if (!this.isDrivingLicenceNumberValidationNew) return;
      // For the last 2 chars box only allow [A-Za-z] chars, backspace(delete) and tab
      const key = event.keyCode;
      if (!((key > 64 && key < 91) || (key > 96 && key < 123) || key === 8 || key === 9)) {
        event.preventDefault();
      }
    },
    onLicenceNumberKeyUp (n) {
      this.limitLicenseNumberInputChars(n);
      const length = typeof this.licence.number[n] === 'number'
        ? this.licence.number[n].toString().length
        : this.licence.number[n].length;
      if (length === this.drivingLicenceNumberRules[n].length) {
        const inputRef = this.$refs[`licenceNumber${n + 1}`];
        if (inputRef) inputRef.focus();
      }
    },
    onLicenceNumberFocus (event, n) {
      const value = event.target.value;

      if (value.length === this.drivingLicenceNumberRules[n].length) {
        event.target.select();
      } else {
        event.target.value = null;
        event.target.value = value;
      }
    },
    async onLicencePaste (event, n) {
      event.preventDefault();

      const value = event.clipboardData.getData('text/plain').replace(/-| /g, '');

      const [fullResult, partialResult] = await Promise.all([
        validate(value, 'required|length:16', { name: 'Full Licence' }),
        validate(value, this.drivingLicenceNumberRules[n], { name: `Licence (part #${n})` }),
      ]);

      if (!fullResult.valid && !partialResult.valid) {
        this.$emit('toast', {
          type: 'error',
          text: [
            fullResult.errors[0],
            partialResult.errors[0],
          ],
        });
        return false;
      }

      if (fullResult.valid) {
        const valueParts = [
          value.substr(0, 5),
          value.substr(5, 6),
          value.substr(11, 3),
          value.substr(14, 2),
        ];

        const results = await Promise.all(
          valueParts.map((value, index) => validate(value, this.drivingLicenceNumberRules[index + 1], { name: `Full Licence (part #${index + 1})` })),
        );

        const invalidResult = results.find(result => !result.valid);
        if (invalidResult) {
          this.$emit('toast', {
            type: 'error',
            text: invalidResult.errors[0],
          });
          return false;
        }

        valueParts.forEach((value, index) => {
          this.licence.number[index + 1] = value;
        });
      }

      if (partialResult.valid) {
        this.licence.number[n] = value;
      }
    },
    prefillLicence (partIndex = null) {
      if (
        this.isLicenceHidden ||
        !this.questionData.licenceNumberYesNo
      ) return;

      const getPartOnePrefill = surname => {
        // Prevents prefill if prefilling string contains diacritic symbols from surname
        if (!licencePrefillNameRegex.test(surname)) return null;

        let result = surname.replace(/-| /g, '').toUpperCase();
        if (result.indexOf('MAC') === 0) result = result.replace('MAC', 'MC');
        result = padEnd(result.substr(0, 5), 5, '9');
        return result;
      };

      if (
        this.stepData.surname &&
        [null, 1].includes(partIndex) &&
        [undefined, false].includes(this.formObserver.fields?.['drivers_licence-number-1']?.touched)
      ) {
        this.licence.number[1] = getPartOnePrefill(this.stepData.surname);
      }

      const getPartTwoPrefill = (dateOfBirth, title) => {
        const isGenderNeutral = title && ['Dr', 'Mx'].includes(this.stepData.title);
        let result = '';
        if (isGenderNeutral) return result;
        const date = new ExtDate(dateOfBirth);
        const year = date.getFullYear().toString();
        const yearDigit = year.substr(year.length - 1, 1);
        const decadeDigit = year.substr(year.length - 2, 1);
        result += decadeDigit;
        let month = date.getMonth() + 1;
        const isWoman = title && this.stepData.title !== 'Mr';
        if (isWoman) month += 50;
        result += padStart(month, 2, 0);
        result += padStart(date.getDate().toString(), 2, 0);
        result += yearDigit;
        return result;
      };

      if (this.stepData.dateOfBirth && [null, 2].includes(partIndex)) {
        this.licence.number[2] = getPartTwoPrefill(this.stepData.dateOfBirth, this.stepData.title);
      }
    },
  },
  setup () {
    const productName = inject('productName');
    const constants = inject('constants');
    const config = inject('config');
    const {
      isDrivingLicenceNumberValidation,
    } = inject('abTastyControllerUI');

    const isDrivingLicenceNumberValidationNew = computed(() => {
      return [constants.DEFAULT_PARTNER, constants.DEFAULT_PARTNER_ALTERNATIVE].includes(config.partner) &&
        ['car'].includes(productName) &&
        isDrivingLicenceNumberValidation.value;
    });

    const drivingLicenceNumberRules = computed(() => {
      return isDrivingLicenceNumberValidationNew.value ? licenceNumberRulesUpdated : licenceNumberRules;
    });

    return {
      isDrivingLicenceNumberValidationNew,
      drivingLicenceNumberRules,
    };
  },
};
</script>

<style lang="scss" scoped>
.licence-number {
  display: flex;
  flex-direction: row;
  max-width: 528px;

  .col {
    padding: 0;

    @media screen and (min-width: 600px) {
      margin: 0 24px;
      flex-shrink: 0;
    }

    &:nth-child(1) {
      flex-grow: 4;
      margin-top: 0;
      margin-left: 0;
    }

    &:nth-child(2) {
      flex-grow: 4;
    }

    &:nth-child(3) {
      flex-grow: 3;
    }

    &:nth-child(4) {
      flex-grow: 2;
      margin-bottom: 0;
      margin-right: 0;
    }

    & + .col {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        width: 10px;
        height: 1px;
        background-color: $clr-outline;
        top: 50%;
        left: -5px;
        transform: translateX(-50%);

        @media screen and (min-width: 600px) {
          width: 32px;
          left: -24px;
        }
      }
    }
  }
}
</style>

<style lang='scss'>
.licence-number {
  @media screen and (max-width: 599px) {
    justify-content: start;
  }

  .col {
    @media screen and (max-width: 599px) {
      margin: 0 5px;
    }
  }

  .v-input__control {
    @media screen and (max-width: 599px) {
      font-size: 14px;
    }
  }

  .v-input__slot {
    @media screen and (max-width: 599px) {
      padding: 5px !important;
      min-height: auto !important;
    }
  }

  input {
    text-transform: uppercase;
  }
}
</style>
